import {
	Input
} from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ValidationForm as Form } from '../';
import { checkContactDuplicacy } from '../../../api/contact/contact.js';
import { generateRouteUrl } from '../../../library/constants/routes.js';
import { GenderSelect, GeneralFieldWrapper, LanguageSelect, PhoneInput, RoleSelect } from '../../inputs';
import PhoneDisplay from '../../inputs/PhoneDisplay.jsx';
import styles from './customer-contact.module.scss';
import { normalizeEmail } from '../../../library/helpers/normalize.js';

const CustomerContactDisabled = ({ data = {} }) => (
	<div className="form-fields-wrapper form-fields-wrapper" style={{ alignItems: 'start' }}>
		<GeneralFieldWrapper label="First Name">{data.first_name}</GeneralFieldWrapper>
		<GeneralFieldWrapper label="Last Name">{data.last_name}</GeneralFieldWrapper>
		<div className=""></div>
		<GeneralFieldWrapper label="Email Address">{data.email}</GeneralFieldWrapper>
		<GeneralFieldWrapper label="Phone Number"><PhoneDisplay code={data.phone_country_code} number={data.phone_number} /></GeneralFieldWrapper>
		<GeneralFieldWrapper label="Mobile Number"><PhoneDisplay code={data.mobile_country_code} number={data.mobile_number} /></GeneralFieldWrapper>
		<GeneralFieldWrapper label="Gender">{data.gender?.name}</GeneralFieldWrapper>
		<GeneralFieldWrapper label="Role in Company">{data.role?.name}</GeneralFieldWrapper>
		<GeneralFieldWrapper label="Language">{data.language?.name}</GeneralFieldWrapper>
	</div>
)

CustomerContactDisabled.propTypes = {
	data: PropTypes.object.isRequired
}

// Email shared with another contact of the same company only warns (shared
// mailbox); another company using it blocks. See server logic/contacts/duplicate-email.md
const duplicacyChecker = async (field, value, originalValue, { customerId, contactId, setWarning, latestValue }) => {
	latestValue.current = value
	setWarning(null)
	//Don't check on update form when value is unchanged
	if (originalValue && value === originalValue) {
		return Promise.resolve()
	}
	const data = await checkContactDuplicacy(field, value, customerId, contactId)
	// A slower, older check must not show its result for a value since replaced.
	if (latestValue.current !== value) {
		return Promise.resolve()
	}
	if (!data || !data.id) {
		return Promise.resolve()
	}
	const link = <Link className="blue" to={generateRouteUrl('ContactDetails', { id: data.id })}>View here</Link>
	const sameCompany = customerId && String(data.customer_id) === String(customerId)
	if (field === 'email' && sameCompany) {
		setWarning(<span className={styles.duplicateWarning}>Another contact of this company already uses this email. You can still save. {link}</span>)
		return Promise.resolve()
	}
	return Promise.reject(<>{field} is already being used{field === 'email' ? ' by another company' : ''} {link}</>)
}

const CustomerContact = ({ children, index, allowedLanguages, getValidationStatus, customerId, ...otherProps }) => {
	// otherProps.initialValues.mobile_country_code = getCountryById(country)?.phone_number_code
	// otherProps.initialValues.phone_country_code = getCountryById(country)?.phone_number_code

	const [form] = Form.useForm()
	const [duplicateWarning, setDuplicateWarning] = useState(null)
	const latestEmail = useRef(null)
	const companyId = customerId ?? otherProps?.initialValues?.customer_id
	const validationRules = {
		first_name: [
			{
				required: true,
				message: 'First Name is required'
			}
		],
		last_name: [
			{
				required: true,
				message: 'Last Name is required'
			}
		],
		email: [
			{
				required: true,
				message: 'Email is required'
			},
			{
				type: 'email',
				message: 'Email is not valid'
			},
			{
				validator: ({ field }, value) => duplicacyChecker(field, value, otherProps?.initialValues?.[field], {
					customerId: companyId,
					contactId: otherProps?.initialValues?.id,
					setWarning: setDuplicateWarning,
					latestValue: latestEmail,
				}),
			}
		],
		gender_id: [
			{
				required: true,
				message: 'Gender is required'
			}
		],
		company_role: [
			{
				required: true,
				message: 'Role is required'
			}
		],
		language_id: [
			{
				required: true,
				message: 'Language is required'
			}
		],
	}
	useEffect(() => {
		if (allowedLanguages.length === 1) {
			form.setFieldValue('language_id', parseInt(allowedLanguages[0]));
		}
	}, [allowedLanguages]);

	if (otherProps.disabled) {
		return (
			<CustomerContactDisabled data={otherProps?.initialValues} />
		)
	}

	return (
		<Form form={form}
			name={`contact-${index}`}
			layout="vertical"
			className={styles.FormWrapper}
			onValidationStatusChange={getValidationStatus}
			validationRules={validationRules}
			validateTrigger={['onChange', 'onBlur']}
			{...otherProps}
		>
			<Form.Item name="first_name" label="First Name" >
				<Input placeholder="First Name" onKeyPress={(event) => {
					if (/[0-9]/.test(event.key)) {
						event.preventDefault();
					}
				}} maxLength={60}/>
			</Form.Item>
			<Form.Item name="last_name" label="Last Name" >
				<Input placeholder="Last Name" onKeyPress={(event) => {
					if (/[0-9]/.test(event.key)) {
						event.preventDefault();
					}
				}} maxLength={60}/>
			</Form.Item>
			<br />
			<Form.Item name="email" label="Email Address" extra={duplicateWarning} getValueFromEvent={(event) => normalizeEmail(event.target.value)}>
				<Input type="email" placeholder="Email" />
			</Form.Item>
			<Form.Item label="Phone Number" required>
				<PhoneInput valuePropName={{ code: 'phone_country_code', number: 'phone_number' }} placeholder="Telephone No." label='Phone Number' required />
			</Form.Item>
			<Form.Item label="Mobile Number">
				<PhoneInput valuePropName={{ code: 'mobile_country_code', number: 'mobile_number' }} placeholder="Mobile No." label='Mobile Number' />
			</Form.Item>
			<Form.Item name='gender_id' label="Gender" >
				<GenderSelect />
			</Form.Item>
			<Form.Item name='company_role' label="Role in Company" >
				<RoleSelect />
			</Form.Item>
			<Form.Item name='language_id' label="Language" >
				<LanguageSelect options={allowedLanguages?.map(lang => parseInt(lang))} />
			</Form.Item>
			{children}
		</Form>
	)
}
//suffixIcon={<Icon component={() => (<img src="/icons/dropdown.svg" />)} styles={{pointerEvents: 'none'}} />}
CustomerContact.propTypes = {
	children: PropTypes.node,
	index: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	getValidationStatus: PropTypes.func,
	form: PropTypes.object,
	allowedLanguages: PropTypes.array,
	customerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

export default CustomerContact
